/**
 * The website's entry point: build the shared view models, mount the lunula shell,
 * and pump one into the other.
 *
 * ── The shape of this file ───────────────────────────────────────────────────
 *
 * Three things, in this order, and the order matters:
 *
 *  1. **The model.** A [StorageRepository] and the backing view models, none of which
 *     knows what a browser is. Everything about what a tab *is*, and everything about
 *     who is signed in, lives there in `:client`, shared with Android and iOS.
 *  2. **The mount.** `mountAppShell` in *source mode* — LunaPin owns the tab and
 *     window list and pushes snapshots; the toolkit renders them and reports gestures
 *     back. The alternative, local mode, would have the toolkit own the tab list and
 *     persist it itself, which is less code and the wrong answer twice over: the
 *     arrangement has to follow the *account* rather than the browser, and the "+"
 *     dropdown has to say "New window", which only a source-mode host can put there.
 *  3. **The pumps.** One collector turning each [WorkspaceBackingViewModel.State] into
 *     a [TabListSnapshot], and one turning each [SessionBackingViewModel.State] into an
 *     account corner and a landing surface.
 *
 * ── Why the sign-in surfaces are driven from here ────────────────────────────
 *
 * The account corner goes into the top bar through the toolkit's own trailing slot, so
 * that is unremarkable. The landing surface is not: it has to *outrank* the whole
 * chrome, so it is mounted on `<body>` from this file rather than into any slot the
 * toolkit offers. See [LandingSurface], which explains why the shell underneath is
 * covered rather than hidden.
 *
 * @see WorkspaceBackingViewModel
 * @see SessionBackingViewModel
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import se.soderbjorn.lunapin.client.ServerEventStream
import se.soderbjorn.lunapin.client.StorageRepository
import se.soderbjorn.lunapin.client.defaultServerUrl
import se.soderbjorn.lunapin.client.viewmodel.SessionBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.StarredFiltersBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.WindowContent
import se.soderbjorn.lunapin.client.viewmodel.Workspace
import se.soderbjorn.lunapin.client.viewmodel.WorkspaceBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.filterSummary
import se.soderbjorn.lunapin.clientserver.HttpLunaPinApi
import se.soderbjorn.lunapin.clientserver.ServerEvent
import se.soderbjorn.lunapin.clientserver.createHttpClient
import se.soderbjorn.lunapin.clientserver.newOriginId
import se.soderbjorn.lunula.web.layout.LayoutPreset
import se.soderbjorn.lunula.web.layout.PaneAction
import se.soderbjorn.lunula.web.layout.PaneMenuItem
import se.soderbjorn.lunula.web.showConfirmDialog
import se.soderbjorn.lunula.web.shell.AppShellHandle
import se.soderbjorn.lunula.web.shell.AppShellSpec
import se.soderbjorn.lunula.web.shell.DefaultPaneGlyph
import se.soderbjorn.lunula.web.shell.InitialPaneGeometry
import se.soderbjorn.lunula.web.shell.PaneAddMenuItem
import se.soderbjorn.lunula.web.shell.PaneOverflowSpec
import se.soderbjorn.lunula.web.shell.PaneSnapshotEntry
import se.soderbjorn.lunula.web.shell.TabListSnapshot
import se.soderbjorn.lunula.web.shell.TabSnapshotEntry
import se.soderbjorn.lunula.web.shell.TabSource
import se.soderbjorn.lunula.web.shell.TopbarAction
import se.soderbjorn.lunula.web.shell.mountAppShell

/**
 * How long the shell waits after a filter stops changing before writing it back.
 *
 * The filter is stored in the workspace, so every keystroke in a list window's search box
 * is in principle a `PUT` to the server. That is the wrong trade twice over — a request
 * per character to persist a search nobody has finished typing, and a sidebar row
 * flickering through `"j"`, `"ja"`, `"jav"` while somebody writes `javascript`.
 *
 * A third of a second: long enough that ordinary typing produces one write, short enough
 * that pausing to read what you typed has already saved it. Nothing is lost if the page
 * goes away inside the window — the filter is still applied on screen, and what fails to
 * survive is a reload, which is exactly what `StorageRepository.putSetting` already says
 * about every other setting written from a paint path.
 */
private const val FILTER_WRITE_DELAY_MS = 350

/**
 * A filter change that has been made on screen but not yet written back.
 *
 * The `setTimeout` handle alone would be enough to *cancel* the write; the filter is
 * carried beside it so the write can also be made to happen **early**, which is what
 * *Clone window* needs — see `flushFilterWrite` in [main].
 */
private class PendingFilterWrite(
    val handle: Int,
    val filter: WindowContent.BookmarkList,
)

/**
 * The `⋮` row that clones a list window, drawn in the toolkit's own hand.
 *
 * Two offset rounded rectangles — the copy glyph everything else in the world uses for
 * this. Same 24-viewBox, 1.8-weight, round-capped strokes as `PaneOverflowMenuIcons`, so
 * it sits beside *Rename window* without a visible seam rather than looking like the one
 * row a different app drew.
 */
private const val CLONE_WINDOW_ICON =
    "<svg viewBox=\"0 0 24 24\" width=\"14\" height=\"14\" fill=\"none\" stroke=\"currentColor\" " +
        "stroke-width=\"1.8\" stroke-linecap=\"round\" stroke-linejoin=\"round\">" +
        "<rect x=\"9\" y=\"9\" width=\"11\" height=\"11\" rx=\"2\"/>" +
        "<path d=\"M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5\"/>" +
        "</svg>"

fun main() {
    // MainScope, and everything in this file shares it: the page is the lifetime. There
    // is no navigation away and back, so nothing here needs a scope it can cancel — a
    // per-window one would be ceremony around a page that only ever ends by closing.
    val scope = MainScope()

    // A relative base URL on this target, which is the whole of the deployment story:
    // the server that served this bundle is the server it talks to, so there is no
    // cross-origin request and no CORS to configure. Read through `defaultServerUrl()`
    // rather than relying on `HttpLunaPinApi`'s default, so that every client answers
    // the "which server?" question in the same one place. See ServerUrl.kt.
    // This tab's own identity on the wire, generated once and used twice: stamped on every
    // request so the server can name the author of a change, and handed to the event stream so
    // it can drop this tab's own writes coming back. **One value, both places** — two would
    // mean every drag of a window re-reading and repainting on top of the drag.
    //
    // Per *tab*, not per browser: two tabs on one account genuinely do need to hear each other.
    val originId = newOriginId()
    val storage = StorageRepository(
        HttpLunaPinApi(defaultServerUrl(), createHttpClient(originId = originId)),
    )
    // The push channel. The browser's carrier is `EventSource` rather than the shared Ktor
    // transport, and that is a requirement rather than a preference — see
    // `BrowserEventSourceTransport`. What it does with what arrives is shared, so a change
    // reaches this tab the same way and at the same moment it reaches a phone.
    val events = ServerEventStream(BrowserEventSourceTransport(), originId)
    val workspaceViewModel = WorkspaceBackingViewModel(storage, scope)
    val sessionViewModel = SessionBackingViewModel(storage, scope)
    // The account's starred filters (LPN-68). One for the app, not one per window: every
    // window's star and the Starred dialog read the same list.
    val starredViewModel = StarredFiltersBackingViewModel(storage, scope)

    // The shell's own handle, for the two things that cannot be said in a snapshot: raising
    // a window the toolkit is already showing, and asking it to read the pane labels again.
    // Forward-declared because the callbacks below close over it and they are built before
    // `mountAppShell` has anything to hand back.
    var shell: AppShellHandle? = null

    /**
     * Open [bookmarkId] for editing — a row's own edit action, and the `e` key on the row
     * `j`/`k` has landed on. **One function behind both**, which is what LPN-10 asked for
     * when it put a deliberately plain *Edit* button on the old rows: the key was to
     * replace that button's path rather than open a second one beside it.
     *
     * Two halves, and the second is not belt-and-braces: a snapshot's `activePaneId`
     * focuses a window but does not re-stack one sitting behind another, and the toolkit
     * reports an ordinary click on a window's body to nobody — so the model's idea of what
     * is focused can already be this editor while the user is looking at the list. See
     * `onAppSettingsActivate` below, which is the same pair for the same reason.
     */
    fun openEditor(bookmarkId: Long) {
        workspaceViewModel.onEditBookmarkActivated(bookmarkId)
        workspaceViewModel.editBookmarkWindowId(bookmarkId)?.let { shell?.bringPaneToFront(it) }
    }

    /**
     * The filter change each window has made but not yet written back.
     *
     * Per window rather than one for the shell: two lists can be filtered at once, and a
     * single entry would mean the second window's typing cancelled the first window's
     * write and its filter was never stored.
     */
    val pendingFilterWrites = mutableMapOf<String, PendingFilterWrite>()

    /**
     * Write [windowId]'s pending filter back **now**, if it has one.
     *
     * The debounce is a kindness to the server, not a claim that the stored filter is
     * allowed to be stale when somebody asks for it. *Clone window* is the ask: it copies
     * the window as the workspace has it, so cloning within a third of a second of turning
     * a knob would otherwise copy the filter as it was *before* the knob — the one gesture
     * where the lag is not invisible, because the two windows end up side by side and
     * visibly disagreeing.
     *
     * Idempotent, and a no-op for a window with nothing in flight.
     */
    fun flushFilterWrite(windowId: String) {
        val pending = pendingFilterWrites.remove(windowId) ?: return
        window.clearTimeout(pending.handle)
        workspaceViewModel.onWindowFilterChanged(windowId, pending.filter)
    }

    // `lateinit` because one of the callbacks below closes over the registry it is being
    // handed to: a window that asks to close itself has to be forgotten by the same
    // registry that built it.
    lateinit var windowBodies: WindowBodies
    windowBodies = WindowBodies(
        storage = storage,
        scope = scope,
        // Both halves, exactly as `onPaneClose` does them: the model drops the window,
        // which is what makes it disappear via the next snapshot, and the registry stops
        // collecting for it. The toolkit's own × goes through `onPaneClose`; this is the
        // same close asked for from inside a window's body.
        onCloseWindow = { windowId ->
            workspaceViewModel.onWindowCloseRequested(windowId)
            windowBodies.dispose(windowId)
        },
        // An editor learns what it is called only after its bookmark arrives, and
        // `paneLabel` is asked synchronously — so the answer changes once, a moment after
        // the window opens, and the shell has to be told to ask again.
        onLabelChanged = { shell?.refresh() },
        onEditBookmark = { openEditor(it) },
        // The filter is the window, so it goes into the stored workspace — but not on every
        // keystroke. See FILTER_WRITE_DELAY_MS. The `refresh()` afterwards is what makes an
        // unnamed window's sidebar row follow its filter: `paneLabel` is a synchronous
        // callback the shell only calls when it repaints, and a snapshot whose tab and pane
        // ids are unchanged does not make it repaint.
        // Only ever read, never stored: the footer asks on each paint, because the answer
        // changes under a window with nothing to tell it.
        isEventStreamConnected = { events.isConnectedFlow.value },
        onFilterChanged = { windowId, filter ->
            pendingFilterWrites.remove(windowId)?.let { window.clearTimeout(it.handle) }
            val handle = window.setTimeout({
                pendingFilterWrites.remove(windowId)
                workspaceViewModel.onWindowFilterChanged(windowId, filter)
                shell?.refresh()
            }, FILTER_WRITE_DELAY_MS)
            pendingFilterWrites[windowId] = PendingFilterWrite(handle, filter)
        },
    )

    // ── Starred filters (LPN-68) ─────────────────────────────────────────────
    //
    // Lunarbor's Starred dialog, opened per window: the list window's star button and ⌘S
    // open it for that window, and picking a row replaces that window's filter. At most one
    // is up at a time — it is a modal.
    var starredDialog: StarredFiltersDialog? = null

    /** The focused window of the showing tab — what ⌘S opens Starred for. */
    fun focusedWindowId(): String? {
        val workspace = workspaceViewModel.stateFlow.value.workspace
        val tab = workspace.tabs.firstOrNull { it.id == workspace.activeTabId } ?: return null
        return tab.activeWindowId ?: tab.windows.firstOrNull()?.id
    }

    /** Open Starred for [windowId], replacing any dialog already up. Lists only. */
    fun openStarred(windowId: String) {
        if (windowBodies.listFilter(windowId) == null) return
        starredDialog?.dispose()
        val dialog = StarredFiltersDialog(
            model = starredViewModel,
            currentFilter = { windowBodies.listFilter(windowId) },
            windowName = { workspaceViewModel.windowName(windowId) },
            onPick = { filter -> windowBodies.replaceFilter(windowId, filter) },
            onClosed = { starredDialog = null },
        )
        starredDialog = dialog
        dialog.mount(starredViewModel.stateFlow.value)
    }

    // ⌘S / Ctrl+S: open Starred for the focused list window — or, with it already open, star
    // or un-star that window's filter, so pressing it twice stars where you are. Lunarbor's
    // chord, for the same gesture. Capture phase, so the browser's own *Save page* never sees
    // it; a window that is not a list leaves the key alone.
    document.addEventListener("keydown", { event ->
        val key = event as KeyboardEvent
        val isSaveChord = (key.metaKey || key.ctrlKey) && !key.altKey && !key.shiftKey &&
            key.key.equals("s", ignoreCase = true)
        if (!isSaveChord) return@addEventListener
        val open = starredDialog
        if (open != null) {
            key.preventDefault()
            key.stopPropagation()
            open.toggleCurrentFromShortcut()
            return@addEventListener
        }
        val windowId = focusedWindowId() ?: return@addEventListener
        if (windowBodies.listFilter(windowId) == null) return@addEventListener
        key.preventDefault()
        key.stopPropagation()
        openStarred(windowId)
    }, true)

    val appHost = document.getElementById("app") as? HTMLElement
        // index.html ships this div; if it is missing the bundle and the page have got
        // out of step, and failing loudly beats mounting into a body nobody styled.
        ?: error("index.html is missing its #app container.")

    // Built before the spec, because the spec has to be handed this view's one stable
    // element — see SignInView.root for what happens to a corner that rebuilds itself.
    val signInView = SignInView(sessionViewModel)
    val landingSurface = LandingSurface(
        shell = appHost,
        // The card's button and the corner's button are the same button, reached through
        // the same function. A second path into the dialog is a second path to keep in
        // step with this one.
        onSignIn = { signInView.startSignIn() },
    )

    // ── The snapshot pump ────────────────────────────────────────────────────
    //
    // `mountAppShell` subscribes asynchronously, inside its own init coroutine, so a
    // snapshot produced before that lands would be dropped. Holding the latest and
    // delivering it on subscribe is what makes the first paint the user's real
    // workspace rather than an empty one that fills in a moment later.
    var push: ((TabListSnapshot) -> Unit)? = null
    var latest: TabListSnapshot? = null

    fun deliver(snapshot: TabListSnapshot) {
        latest = snapshot
        push?.invoke(snapshot)
    }

    val spec = AppShellSpec(
        rootContainer = appHost,
        title = "LunaPin",
        // The account, not the browser — and this covers considerably more than the
        // window layout. Everything the shell remembers goes through here:
        //
        //   · the theme picked for the dark slot and for the light one
        //   · the appearance (Auto / Dark / Light), i.e. the sun-moon toggle
        //   · any themes the user built in the theme manager, and the ones they starred
        //   · corner roundness, spacing density and selection style
        //   · the font face and size chosen for each surface in the Appearance sidebar
        //   · which sidebar sections are collapsed, and how wide the sidebar was dragged
        //   · custom hotkey bindings
        //   · where every window sits, per tab
        //
        // No `settingsHost` is supplied, deliberately: the toolkit then owns theme
        // resolution with its own host, mounts its own theme-manager and appearance
        // controls, and persists all of the above through this Persister with no code
        // here. That is the arrangement Lunicle uses too. An app supplies a host of its
        // own only when it needs to resolve themes outside the toolkit, which LunaPin
        // does not.
        //
        // That property is *not* what puts a gear in the top bar, despite its name and its
        // documentation: the toolkit renders its appearance button unconditionally and
        // `settingsHost` only chooses which theme host that button's sidebar writes
        // through. LunaPin's own settings gear is `onAppSettingsActivate`, below.
        //
        // The one thing NOT here is the tab and window *list*, which is LunaPin's own
        // model and is written by the view model under its own key. See ServerPersister
        // and UiSettingKeys.
        persister = ServerPersister(storage),
        // ── The faces, and why naming them is the whole fix ──────────────────
        //
        // These three seams are the toolkit's deploy-time font defaults, and they are the
        // *only* thing that puts the `--dt-font-*` custom properties on `:root` for an
        // account that has never picked a font. `applyHostFontVars` in the toolkit's
        // `AppShellMount` resolves each surface as
        //
        //     the user's own pick  >  the value returned here  >  (removed)
        //
        // and that last branch is a literal `removeProperty` — so an app that names
        // nothing leaves `var(--dt-font-prop)` / `var(--dt-font-display)` *undefined*,
        // every rule that reads one becomes invalid at computed-value time, and the type
        // falls back through inheritance to whatever the document root happens to say.
        // For LunaPin that was the browser's default serif on every surface outside the
        // toolkit's own frame, which is a stylesheet full of correct tokens rendering in
        // Times. Naming a key here is what makes those tokens exist.
        //
        // `systemProp` — the toolkit's own system-sans preset — for all three, which is a
        // real choice rather than a placeholder: it is the same stack `.dt-app-frame` and
        // `.dt-modal` hardcode, so LunaPin's own surfaces match the chrome around them
        // instead of being a second face inside the same window. One line to change when
        // LunaPin has a face of its own, and a user's pick in Appearance still outranks it.
        //
        // Mono is deliberately absent, and now that is a choice rather than a gap: the
        // toolkit grew a `defaultMonoFontFamily` seam beside these three, and LunaPin
        // declines it. The reason it declines is the reason the seam does not fall through
        // to the chrome font either — code is not chrome, and the one rule that wants a
        // monospaced face carries `var(--dt-font-mono, ui-monospace, …)` in `lunula.css`,
        // so an unset variable paints the platform's code face rather than nothing. A user
        // who picks a mono font in Appearance still gets it, and it is now stored.
        // Sizes are absent too — the toolkit's are right.
        defaultChromeFontFamily = { LUNAPIN_DEFAULT_FONT_PRESET },
        defaultProseFontFamily = { LUNAPIN_DEFAULT_FONT_PRESET },
        defaultDisplayFontFamily = { LUNAPIN_DEFAULT_FONT_PRESET },
        // The account corner. One stable element, appended afresh by every top-bar
        // rebuild — which is what `TopbarAction.element` is for and why SignInView holds
        // its root rather than building one per render.
        extraTopbarTrailing = listOf(TopbarAction.custom(signInView.root)),
        // LunaPin's settings gear. The toolkit renders the button — its own cog glyph, in
        // its own cluster, on the correct side of the divider — and hands the press
        // straight here instead of opening a sidebar of its own. See SettingsWindow, which
        // records why this is the right one of the toolkit's three settings affordances.
        //
        // The press goes to the view model rather than opening anything from here, and
        // that is the whole of LPN-9: what the gear does — focus the settings window if
        // one is open anywhere, otherwise put a new one in the showing tab — is a rule
        // about the workspace, so it belongs where the workspace lives and Android and iOS
        // inherit it.
        //
        // The `bringPaneToFront` afterwards is the web half of "focus", and it is not
        // belt-and-braces: a snapshot's `activePaneId` only *focuses* a window, it does not
        // re-stack one sitting behind another — and, more to the point, the toolkit reports
        // an ordinary click on a window's body to nobody (`TabSource` has no pane-focus
        // callback), so the model's idea of which window is focused can already be the
        // settings window while the user is looking at another one. The gear's commit is
        // then a no-op that pushes no snapshot, and without this line the press would do
        // nothing visible at all. It is a no-op itself when the window is not in the
        // showing tab — the snapshot's tab switch carries the focus in that case.
        //
        // Not gated by `isAppSettingsAvailable`: signed out, the landing surface covers the
        // whole shell and marks it inert, so the gear is already unreachable. Gating it as
        // well would be a second mechanism enforcing the same thing, and the two would
        // eventually disagree about which states count as signed out.
        onAppSettingsActivate = {
            workspaceViewModel.onSettingsActivated()
            workspaceViewModel.settingsWindowId()?.let { shell?.bringPaneToFront(it) }
        },
        // The kind comes from the workspace the view model holds, which is the only
        // place that knows it: the toolkit hands back an id and nothing else, by
        // design, since what a window *contains* is the app's question. Read
        // synchronously off the StateFlow rather than mirrored into a local map, so
        // there is one copy of the answer.
        paneContent = { windowId ->
            windowBodies.contentFor(windowId, workspaceViewModel.windowContent(windowId))
        },
        // ── What a window is called ──────────────────────────────────────────
        //
        // Two halves, and the order between them is the whole rule: **the user's own name
        // wins, and a window that has never been named says what it shows.**
        //
        // A list used to be "Window 3", derived from its id, because there was nothing
        // better — a label reading "Bookmarks" on every row of the sidebar is no label at
        // all, and a number is at least unique. Now a window *is* a saved filter, so the
        // filter is the name: `#recipes`, or `#read-later, not #gaming`, or "All bookmarks"
        // for one that has not been narrowed. Derived rather than stored, which is what
        // makes it follow the knobs — change the filter and the sidebar row changes with
        // it, and that is what keeps four open windows readable.
        //
        // `filterSummary` is the same function the expanded rail's footer reads the filter
        // back with. One function, two call sites, in `:client` — so the words are testable
        // without a browser and are the ones Android and iOS would want the day they show a
        // filter.
        //
        // There is only ever one settings window (see `onSettingsActivated`), so a bare
        // "Settings" is unambiguous. An editor is named after the bookmark it edits, and
        // that name is not knowable here: it lives on the server, this callback is
        // synchronous, and the window is drawn before the fetch answers. So the body
        // reports its own name as it learns it and this reads the last thing it said,
        // falling back to a neutral word for the moment in between. `onLabelChanged` is
        // what makes the fallback temporary.
        paneLabel = { _, windowId ->
            workspaceViewModel.windowName(windowId)
                ?: when (val content = workspaceViewModel.windowContent(windowId)) {
                    is WindowContent.BookmarkList -> filterSummary(content)
                    WindowContent.Settings -> "Settings"
                    is WindowContent.EditBookmark -> windowBodies.label(windowId) ?: "Bookmark"
                }
        },
        // Every window gets the `⋮`, and the toolkit owns most of what is in it: it offers
        // *Rename window* and *Move to tab ▸*, dropping each one the host has not wired.
        // Both are wired — `paneRename` just below and `TabSource.onPaneMove` further down
        // — so both are drawn, and this callback says nothing about either. The move
        // submenu's contents are the toolkit's too: every other tab in the live snapshot,
        // in strip order, collapsing to one greyed-out row when there is nowhere to go.
        //
        // LunaPin adds one row, and only to lists. *Clone window* on the settings window or
        // an editor would offer to make a second of something there is one right number of
        // — see `onWindowCloned`, which declines the same request from any host — and an
        // item that exists to be a mistake is worse than one that is not there. Asked at
        // menu-*open* time, so "is this a list?" is answered against the window as it is
        // now rather than as it was when the pane was drawn.
        //
        // Placed BELOW, so *Clone window* sits under the two built-ins. The toolkit splices
        // host rows as one block above or below rather than letting a host interleave, and
        // a clone row under a move row is a smaller thing to want than a hand-rolled menu
        // is to own. Note that *Move to tab* is offered for every kind, including the two
        // *Clone window* declines: there is still exactly one settings window and one
        // editor per bookmark after a move — moving does not make a second of anything.
        // The star (LPN-68), on list windows only — filled when the window's filter is
        // starred. Re-asked on every shell refresh, which a filter write and a change to the
        // starred list both trigger.
        paneActions = { _, windowId ->
            // The live filter when the body exists, the stored one before it does — the
            // header can be drawn before the body is asked for.
            val filter = windowBodies.listFilter(windowId)
                ?: workspaceViewModel.windowContent(windowId) as? WindowContent.BookmarkList
            if (filter == null) {
                emptyList()
            } else {
                val starred = starredViewModel.isStarred(filter)
                listOf(
                    PaneAction(
                        iconHtml = if (starred) STAR_FILLED_ICON else STAR_ICON,
                        tooltip = "Starred filters (⌘S)",
                        handler = { openStarred(windowId) },
                        isActive = starred,
                        extraClass = "lp-pane-action-starred",
                    ),
                )
            }
        },
        paneOverflowMenu = { _, windowId ->
            PaneOverflowSpec(
                extraItems = when (workspaceViewModel.windowContent(windowId)) {
                    is WindowContent.BookmarkList -> listOf(
                        PaneMenuItem(
                            label = "Clone window",
                            iconHtml = CLONE_WINDOW_ICON,
                            handler = {
                                // Before the clone, not after: the copy is taken from the
                                // stored workspace, and a filter still sitting in the
                                // debounce is one the copy would be missing.
                                flushFilterWrite(windowId)
                                workspaceViewModel.onWindowCloned(windowId)
                            },
                        ),
                    )
                    else -> emptyList()
                },
            )
        },
        // The host's half of a rename, and the only half: the toolkit arms its own inline
        // editor on the pane title and hands back what was committed. **No toolkit change
        // was needed** — `paneRename` and `canRenamePane` were already there, and LunaPin
        // simply had nothing to store a name in until `WorkspaceWindow.name` existed.
        //
        // `refresh()` afterwards for `onFilterChanged`'s reason: a snapshot with the same
        // ids in it does not make the shell re-ask for labels, so the row would keep its
        // old name until something else repainted.
        paneRename = { tabId, windowId, name ->
            workspaceViewModel.onWindowRenamed(tabId, windowId, name)
            shell?.refresh()
        },
        // Which is what makes clearing the field mean something rather than being read as a
        // cancel. LunaPin's labels have a real "no custom name" state — see
        // `WorkspaceWindow.name`, where null is "never named, follow the filter" — so
        // emptying the box is how somebody asks for the derived name back.
        allowEmptyPaneRename = true,
        // The toolkit's own "Close pane — are you sure?" dialog, off.
        //
        // It defaults on, and on it asked the same question of every window every time: a
        // list window, which is a filter that can be rebuilt in four clicks and is written
        // back to the workspace anyway; the settings window, which holds nothing at all;
        // and an editor nobody had typed into. A confirmation that fires when there is
        // nothing to confirm is not a safety net, it is a keystroke — and it teaches people
        // to dismiss the one that *does* matter without reading it.
        //
        // What replaces it is `onPaneClose` asking the window itself whether closing it
        // would lose anything, which is exactly the arrangement this flag is documented for.
        // The dialog that survives is one dialog, in one place, for the one case.
        confirmPaneClose = false,
        // ── Free-floating, not auto-tiled ────────────────────────────────────
        //
        // The toolkit's default is `Auto`: every window a tab gains re-tiles every window
        // it already had. That is right for a terminal, where the panes are peers. It is
        // wrong here, and the gesture that shows why is *Edit bookmark*: clicking a row
        // halves the list behind it and moves every card the user was reading, so the
        // thing they clicked is no longer where they clicked it. The same is true of the
        // gear, and of anything else that will ever open over the list.
        //
        // In `Custom` a window keeps the geometry it was seeded at until somebody drags
        // it, so opening one never moves another — the list is the base, and editors and
        // settings arrive on top of it. The layout dropdown still offers Auto and the rest
        // to anyone who wants a tiled arrangement, and the toolkit persists that choice
        // under LAYOUT_STATE through the same account storage the workspace rides; this
        // only decides where a tab the stored state has never heard of starts. Lunicle
        // made the same call for the same reason — see its `defaultLayoutPreset`.
        defaultLayoutPreset = LayoutPreset.Custom,
        // A list opens filling the pane area as an ordinary window, NOT maximised, and the
        // distinction is the whole point: maximised is a *mode* that suppresses its
        // siblings, so an editor opened over a maximised list would have nowhere to appear.
        // At 1.0 × 1.0 from the origin it is simply the bottom of the stack, and everything
        // else lands on top of it.
        //
        // Settings is seeded large, and off the origin so the list stays visible behind it.
        // At the toolkit's 45 % × 55 % it opened as a tall slot with a scrollbar: the
        // window is a column of panels — provider, behaviour, cards, backfill, import, the
        // bookmarklet — and none of them are what somebody came for more than the others,
        // so the honest default is one you can read most of without scrolling. It is still
        // an ordinary window rather than maximised, for the reason the list is: everything
        // stays draggable and closable over it.
        //
        // Editors keep the toolkit's own cascade seed, origin included, so two opened in a
        // row are offset rather than stacked exactly.
        paneInitialGeometry = { _, windowId ->
            when (workspaceViewModel.windowContent(windowId)) {
                is WindowContent.BookmarkList ->
                    InitialPaneGeometry(widthPct = 1.0, heightPct = 1.0, xPct = 0.0, yPct = 0.0)
                WindowContent.Settings ->
                    InitialPaneGeometry(widthPct = 0.8, heightPct = 0.9, xPct = 0.1, yPct = 0.05)
                else -> null
            }
        },
        tabSource = TabSource(
            subscribe = { p ->
                push = p
                latest?.let(p)
            },
            onSelect = { workspaceViewModel.onTabSelected(it) },
            // Wired, which is what makes the toolkit render its own "New tab" row in the
            // "+" dropdown, below our window row. LunaPin does not need Lunicle's trick
            // of supplying that row itself to control the menu's order — with one window
            // kind there is nothing to order.
            onAdd = { workspaceViewModel.onTabAdded() },
            onClose = { workspaceViewModel.onTabClosed(it) },
            onRename = { id, label -> workspaceViewModel.onTabRenamed(id, label) },
            onReorder = { source, target, before ->
                workspaceViewModel.onTabReordered(source, target, before)
            },
            onPaneSelect = { tabId, windowId -> workspaceViewModel.onWindowSelected(tabId, windowId) },
            // The other half of the same fact, and the one that had been missing: a
            // *sidebar* click arrives above as `onPaneSelect`, a click inside a window
            // arrives here. Without it the workspace never learned which window the user
            // was actually working in, and — because every snapshot re-asserts
            // `activeWindowId` — the focus ring and the sidebar highlight snapped back to
            // whatever had been focused when the page loaded, one beat after every click.
            //
            // `onWindowFocused` rather than `onWindowSelected`: the toolkit has already
            // moved focus by the time this arrives and the tab cannot have changed, which
            // is exactly the distinction that method exists to draw.
            onPaneFocused = { _, windowId -> workspaceViewModel.onWindowFocused(windowId) },
            onPaneClose = { tabId, windowId ->
                // Both halves, and both are needed: the model drops the window (which is
                // what makes it disappear, via the next snapshot) and the registry stops
                // collecting for it. Nothing else tells WindowBodies a window has gone.
                val close = {
                    workspaceViewModel.onWindowClosed(tabId, windowId)
                    windowBodies.dispose(windowId)
                }
                // Asked only of a window that can answer yes — see
                // `WindowBodies.hasUnsavedChanges` and `confirmPaneClose` below. A list
                // window and the settings window hold nothing that closing can lose, and
                // an editor nobody has typed into holds nothing either; all three close on
                // the press, which is what the ✕ looked like it would do all along.
                if (windowBodies.hasUnsavedChanges(windowId)) {
                    showConfirmDialog(
                        title = "Discard changes?",
                        // The window's own name is the bookmark's title, and the title is
                        // one of the three things that may be unsaved — so the dialog says
                        // what is at stake rather than naming a window that may be called
                        // something the user has just stopped agreeing with.
                        message = "This bookmark has changes that have not been saved. " +
                            "Closing the window discards them.",
                        confirmLabel = "Discard",
                        cancelLabel = "Keep editing",
                        destructive = true,
                        onConfirm = close,
                    )
                } else {
                    close()
                }
            },
            // *Move to tab ▸*, from the `⋮` menu the toolkit draws. The toolkit builds the
            // destination list and reports the choice; it deliberately does not touch the
            // tab tree itself, for the reason it does not on a close or an add — LunaPin
            // owns the tree in source mode, and a toolkit that moved the window
            // optimistically would fight the snapshot that follows.
            //
            // No `refresh()` afterwards, unlike `paneRename`: this snapshot has different
            // pane ids in two of its tabs, so the shell repaints and re-asks for labels of
            // its own accord. It also needs no `flushFilterWrite` — see the intent, which
            // says why a move differs from a clone there.
            //
            // The source tab id is passed through rather than dropped, though LunaPin
            // could look it up: a window id names one window here (ids are allocated, not
            // derived — see `Workspace.nextWindowId`), so the lookup would be total. Using
            // what the toolkit reports keeps the intent honest about which `⋮` was opened,
            // and matches `onPaneClose`'s shape immediately above.
            onPaneMove = { tabId, windowId, targetTabId ->
                workspaceViewModel.onWindowMovedToTab(tabId, windowId, targetTabId)
            },
            // Pressing "+" itself, without going near the dropdown, does the one thing
            // there is to do. The kind is named rather than defaulted — there is more
            // than one now, and the settings window is not one you ask a menu for.
            onPaneAdd = { tabId ->
                workspaceViewModel.onWindowAdded(tabId, WindowContent.BookmarkList())
            },
            paneAddMenuItems = { tabId ->
                // The ticket, spelled out: *"There should only be one type of window
                // possible to create and it's just called 'New window' in the menu
                // option, no other variations."* So exactly one row, and it is the same
                // action the "+" performs on click — marked `isDefault` so the menu says
                // so, rather than leaving the button's own behaviour invisible.
                //
                // Re-evaluated on every menu open and every topbar rebuild. Returning an
                // empty list would hide the "+" entirely (see the toolkit's
                // `shouldShowNewPaneButton`); there is nothing to gate on yet, so it
                // never does.
                listOf(
                    PaneAddMenuItem(
                        id = "new-window",
                        label = "New window",
                        // The toolkit's own window glyph. Using it rather than an SVG of
                        // our own keeps this row looking like the chrome around it, and
                        // it is the same mark the window's sidebar row already wears.
                        iconHtml = DefaultPaneGlyph,
                        isDefault = true,
                        onSelect = {
                            workspaceViewModel.onWindowAdded(tabId, WindowContent.BookmarkList())
                        },
                    ),
                )
            },
        ),
        // ── Putting back what a rerender takes ───────────────────────────────
        //
        // Fired at the end of every toolkit rerender pass, and LunaPin needs it for one
        // thing: **a bookmark list's scroll position**. `LayoutRenderer` rebuilds the pane
        // subtrees whenever the tiling changes, which moves each window's body in the DOM,
        // and a scroller that is moved is one the browser silently resets to the top.
        //
        // The gesture that shows it is the ordinary one — opening a second window while
        // halfway down a list — and the symptom is worse than losing your place: the list's
        // rows are virtualised and positioned for where the reader *was*, so a reset scroller
        // paints nothing at all until something else happens to repaint it.
        //
        // A resize would have covered most of it, and this exists because it does not cover
        // all of it: a window opening into the other column re-tiles this pane without
        // changing its width, so nothing else here would ever hear about it.
        onAfterRefresh = { windowBodies.onShellRefreshed() },
    )

    shell = mountAppShell(spec, scope)

    scope.launch {
        workspaceViewModel.stateFlow.collect { state ->
            // Nothing is pushed until the stored workspace has been read. The toolkit
            // would render the pre-restore state as "no tabs" and then replace it, which
            // is a visible flash of an empty app on every load.
            if (!state.isRestored) return@collect
            deliver(state.workspace.toSnapshot())
        }
    }
    scope.launch { workspaceViewModel.run() }

    // The starred list changing — here, in another tab, on another device — repaints the open
    // dialog and every window's star.
    scope.launch {
        starredViewModel.stateFlow.collect { state ->
            starredDialog?.render(state)
            shell?.refresh()
        }
    }
    scope.launch { starredViewModel.run() }

    // ── The sign-in pump ─────────────────────────────────────────────────────
    //
    // One state, two surfaces, no decisions taken here: which button exists and what
    // every word on the landing card says was settled in `:client`.
    scope.launch {
        sessionViewModel.stateFlow.collect { state ->
            signInView.render(state)
            landingSurface.render(state)
        }
    }
    scope.launch { sessionViewModel.run() }

    // ── A fresh boot when an account arrives ─────────────────────────────────
    //
    // The shell reads the persister exactly once, *while it mounts*, and on a signed-out
    // load that read answers with nobody's settings. So a session arriving afterwards
    // gives a shell holding the defaults for an account that has stored a theme and a
    // set of window positions — and the failure is worse than cosmetic: the toolkit
    // writes its whole layout back on the first drag, so the account's real arrangement
    // would be overwritten by the defaults it never chose. Silent, delayed, and it
    // destroys something. A reload costs a beat and makes every sign-in land in the
    // ordinary boot path, which is the path that is known to work.
    //
    // Signing *out* needs none of this, and the asymmetry is the rule stated properly:
    // an account arriving after the mount needs a fresh boot because there is something
    // to read; an account leaving needs nothing, because there is nothing left to read
    // and the landing surface is already covering the shell that holds the stale copy.
    //
    // The right fix one layer down is a toolkit that can be asked to re-read its
    // persister. Until there is one, this is the seam that would be deleted.
    scope.launch {
        var resolved = false
        var accountId: Long? = null
        storage.sessionFlow.collect { session ->
            if (session == null) return@collect
            val id = session.user?.id
            if (!resolved) {
                // The load's own answer, whatever it is. Reloading here would be a loop.
                resolved = true
                accountId = id
                return@collect
            }
            if (id != null && id != accountId) window.location.reload()
        }
    }

    // ── The push channel ─────────────────────────────────────────────────────
    //
    // What arrives is applied to the repository, which is what every window already reads
    // through — one arrival path, which is the whole of `ServerEvent`'s "identity, not
    // payload" argument. The list windows re-read because `BookmarkListBackingViewModel`
    // collects the repository's change flow; the workspace re-restores because
    // `WorkspaceBackingViewModel` collects its settings revision. Nothing here decides
    // anything, and nothing here is web-specific except the transport.
    //
    // **The toolkit's own layout is the one thing this cannot repaint.** `AppShellMount`
    // reads its `Persister` once, while it mounts, and there is no way to ask it to read
    // again — the same limitation the sign-in reload above already works around. So a window
    // *moved on a phone* does not move in an open browser until the page is reloaded, while
    // every other direction is live. That is a toolkit change (`shell.reloadPersistedState()`)
    // rather than something this file can fix, and pretending otherwise by reloading the page
    // on every layout event would be a page that reloads whenever anybody drags a window.
    scope.launch {
        events.events.collect { event ->
            when (event) {
                is ServerEvent.BookmarksChanged -> storage.onBookmarksChanged(event.ids)
                is ServerEvent.UiSettingChanged -> storage.refreshSettings()
                is ServerEvent.AccountSettingsChanged -> storage.onAccountSettingsChanged()
            }
        }
    }
    // Every successful connect, not only the first: the channel carries no replay, so a tab
    // that was asleep or offline has missed whatever was said. See `ServerEventStream`.
    scope.launch {
        events.connectionsFlow.collect { connections ->
            if (connections == 0) return@collect
            storage.refreshSettings()
            storage.onBookmarksChanged(emptyList())
            storage.onAccountSettingsChanged()
        }
    }
    scope.launch { events.run() }

    // Last, and deliberately: it is what completes the read the persister and the
    // workspace restore are both waiting on, so everything above is already listening
    // when the answer arrives.
    scope.launch { storage.start() }
}

/**
 * LunaPin's workspace, as the toolkit's view of it.
 *
 * Identity only — which tabs exist, which windows are in them, which is focused.
 * Geometry is deliberately absent: the toolkit owns where windows sit and persists it
 * itself through the [ServerPersister]. See [PaneSnapshotEntry], whose one field is the
 * whole of the contract.
 */
private fun Workspace.toSnapshot(): TabListSnapshot = TabListSnapshot(
    tabs = tabs.map { tab ->
        TabSnapshotEntry(
            id = tab.id,
            label = tab.name,
            panes = tab.windows.map { PaneSnapshotEntry(it.id) },
            // Falling back to the first window rather than leaving it null: a tab whose
            // windows exist but none of which is focused has no keyboard target, so the
            // first arrow-key press goes nowhere.
            activePaneId = tab.activeWindowId ?: tab.windows.firstOrNull()?.id,
        )
    },
    activeTabId = activeTabId,
)
